@use '~/theme/theme' as *;
@use '~/theme/typography';

.input {
  display: flex;
  border-radius: 8px;
}

.inputWrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .iconButtons {
    position: absolute;
    top: 50%;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 2px;
    transform: translateY(-50%);
  }
}

.datePickerField {
  --border-color: #{color(grey-600)};

  .inputWrapper {
    border-radius: 8px;
    border: 1px solid var(--border-color);
    background: white;
    width: 100%;

    &[data-focused] {
      outline: 1px solid var(--border-color);

      --border-color: #{color(violet-600)};
    }

    &[data-invalid] {
      --border-color: #{color(lobster-800)};
    }
  }

  .selectOnly {
    cursor: pointer;

    .input {
      cursor: pointer;
      border: none;
      outline: none;
      font-family: 'Public Sans', sans-serif;
    }

    &:hover {
      --border-color: #{color(violet-600)};
    }
  }

  &[data-open] {
    .selectOnly {
      outline: 1px solid var(--border-color);

      --border-color: #{color(violet-600)};
    }
  }

  &[data-disabled] {
    .inputWrapper {
      background-color: color(grey-100);
      color: color(grey-500);

      --border-color: #{color(grey-400)};
    }

    .input {
      cursor: default;
      background-color: transparent;
      color: color(grey-500);
    }

    .selectOnly {
      cursor: default;
    }
  }
}

.datePickerField[data-size='small'] {
  .input {
    @include typography.caption;
    @include typography.medium;

    padding: 6px 12px;

    ::placeholder {
      @include typography.caption;
      @include typography.medium;

      color: color(grey-600);
    }
  }

  &:has(.control, .clear) .input {
    padding-right: 32px;
  }

  &:has(.control):has(.clear) .input {
    padding-right: 56px;
  }

  .iconButtons {
    right: 3px;
  }

  .control,
  .clear {
    width: 24px;
    height: 24px;
    padding: 0;
    border-radius: 6px;
  }
}

.datePickerField[data-size='medium'] {
  .input {
    @include typography.body2;
    @include typography.regular;

    padding: 7px 12px;

    &::placeholder {
      @include typography.body2;
      @include typography.regular;

      color: color(grey-600);
    }
  }

  &:has(.control, .clear) .input {
    padding-right: 40px;
  }

  &:has(.control):has(.clear) .input {
    padding-right: 72px;
  }

  .iconButtons {
    right: 2px;
  }

  .control,
  .clear {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 6px;
  }
}

.datePickerField[data-size='large'] {
  .input {
    @include typography.body1;
    @include typography.regular;

    padding: 11px 12px;

    ::placeholder {
      @include typography.body1;
      @include typography.regular;

      color: color(grey-600);
    }
  }

  &:has(.control, .clear) .input {
    padding-right: 56px;
  }

  &:has(.control):has(.clear) .input {
    padding-right: 96px;
  }

  .iconButtons {
    right: 4px;
  }
}

.segment {
  &[data-focus-visible],
  &[data-focused] {
    outline: 2px solid color(violet-600);
    outline-offset: 1px;
    border-radius: 2px;
  }
}

.hideSegment {
  visibility: hidden;
}

.popover {
  overflow: auto;

  .dialog {
    display: grid;

    &:has(.presetDates) {
      grid-template-columns: 280px 1fr;
    }
  }

  .presetDates {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: spacing(3) 0;
    border-right: border(grey-300);
    align-items: stretch;
    min-height: 0;
    gap: spacing(4);
  }

  .presetTitle {
    color: color(violet-600);
    display: flex;
    justify-content: space-between;
    padding: 0 spacing(3);
  }

  .iconFlash {
    color: color(orange-600);
  }

  .presetList {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: spacing(0.5);
  }

  .preset {
    font-family: 'Public Sans', sans-serif;
    padding: spacing(1.5) spacing(3);
    background: white;
    border: none;
    cursor: pointer;
    text-align: left;
    display: flex;
    flex-direction: column;

    &[data-focus-visible] {
      outline: 2px solid color(violet-600);
      border-radius: 4px;
    }

    &:hover {
      background: color(violet-100);
    }
  }

  .label {
    @include typography.body2;
    @include typography.semibold;
  }

  .detail {
    @include typography.caption;
    @include typography.medium;

    color: color(grey-600);
  }

  .grid {
    border-collapse: separate;
    border-spacing: 4px;
    padding: spacing(1) spacing(2) spacing(5);
    min-height: 450px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: spacing(3) spacing(2);
  }

  .heading {
    @include typography.body1;
    @include typography.semibold;
  }

  .headerCell {
    @include typography.body2;
    @include typography.semibold;

    color: color(grey-600);
  }

  .calendarCell {
    @include typography.body2;
    @include typography.regular;

    transition: background 300ms;
    color: color(grey-800);
    border-radius: 20px;
    display: flex;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin: 2px;
    margin-top: spacing(2);

    &[data-focus-visible],
    &[data-focused] {
      outline: 2px solid color(violet-600);
      outline-offset: 2px;
    }

    &[data-outside-month] {
      color: color(grey-500);
    }

    &[data-hovered] {
      background: color(violet-100);
    }

    &[data-selected] {
      background: color(violet-500);
      color: white;
    }
  }

  .today {
    background: color(grey-200);
  }

  .disabled {
    color: color(grey-500);
    cursor: default;
  }
}
